import "../types/axios.d.ts";
import axios, { AxiosResponse, isAxiosError } from "axios";
import { BaseWidgetAttributes } from "../features/widget-attributes/baseWidgetSchema";
import { logError } from "./sentry";

export const apiClient = axios.create({});

type ApiClientConfig = Pick<BaseWidgetAttributes, "api" | "langue" | "partenaireId"> & {
    jwt?: string;
};

const shouldCaptureError = (error: unknown): boolean => {
    if (!isAxiosError(error) || !error.response) return true;
    const config = error.config;
    const httpCodeStatus = error.response.status;
    const skipHttpCodeStatuses = config?.skipLogHttpErrors;

    return !(Array.isArray(skipHttpCodeStatuses) && skipHttpCodeStatuses.includes(httpCodeStatus));
};

/**
 * Initialise le client API avec la configuration fournie.
 *
 * @param {ApiClientConfig} config - La configuration pour le client API.
 * @param {string} config.api - L'URL de base de l'API.
 * @param {string} [config.langue] - La langue à utiliser pour les requêtes.
 * @param {string} [config.partenaireId] - L'ID du partenaire pour les requêtes.
 * @param {string} [config.jwt] - JWT à utiliser pour l'authentification.
 */
export const initApiClient = (config: ApiClientConfig) => {
    const { api, jwt, langue, partenaireId } = config;
    apiClient.defaults.baseURL = api;
    apiClient.defaults.headers["Resamare-Langue"] = langue || "fr";

    if (partenaireId) {
        apiClient.defaults.headers["Resamare-Partenaire-Id"] = partenaireId;
    }

    if (jwt) {
        apiClient.defaults.headers["Authorization"] = `Bearer ${jwt}`;
    }

    apiClient.interceptors.response.clear();
    apiClient.interceptors.response.use(
        (response: AxiosResponse) => response,
        (error) => {
            if (shouldCaptureError(error)) {
                logError(error);
            }
            return Promise.reject(error);
        },
    );
};
